/**
 * 足球教练战术解说视频
 * 
 * 基于昨天的 FootballTactics 效果，创建教练指导/解说视频
 * 
 * 视频结构：
 * 1. 开头（3-5秒）：标题 + 教练形象 + 足球场背景
 * 2. 主体（15-20秒）：战术板演示 + 球员跑位 + 标注
 * 3. 结尾（3-5秒）：总结 + Logo
 * 
 * 配音：严肃教练风格
 * 
 * @author 龙虾菌 🦞
 * @date 2026-03-10
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Sequence,
  Audio,
  staticFile,
} from 'remotion';

// ==================== 类型定义 ====================

interface CoachCommentaryProps {
  audioIntro?: string;
  audioMain?: string;
  audioOutro?: string;
}

// ==================== 开场组件 ====================

const OpeningScene: React.FC<{
  frame: number;
  fps: number;
}> = ({ frame, fps }) => {
  // 标题入场动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], {
    extrapolateRight: 'clamp',
  });
  
  const titleScale = spring({
    frame,
    fps,
    config: {
      damping: 12,
      stiffness: 80,
    },
  });
  
  // 教练图标旋转
  const coachRotation = interpolate(frame, [0, 60], [-180, 0], {
    extrapolateRight: 'clamp',
  });
  
  // 副标题淡入
  const subtitleOpacity = interpolate(frame, [20, 40], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1a472a 0%, #2d5a27 50%, #1a472a 100%)',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 装饰性背景图案 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0.1,
          background: `
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 80px,
              rgba(255, 255, 255, 0.1) 80px,
              rgba(255, 255, 255, 0.1) 82px
            )
          `,
        }}
      />
      
      {/* 教练图标 */}
      <div
        style={{
          position: 'absolute',
          top: 150,
          transform: `rotate(${coachRotation}deg)`,
          fontSize: 160,
          filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))',
        }}
      >
        🏃‍♂️
      </div>
      
      {/* 主标题 */}
      <div
        style={{
          marginTop: 200,
          padding: '20px 60px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          transform: `scale(${titleScale})`,
          opacity: titleOpacity,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <h1
          style={{
            fontSize: 96,
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #1a472a 0%, #4CAF50 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0,
            letterSpacing: 6,
          }}
        >
          战术分析
        </h1>
      </div>
      
      {/* 副标题 */}
      <div
        style={{
          marginTop: 40,
          padding: '12px 30px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          borderRadius: 12,
          opacity: subtitleOpacity,
          border: '2px solid rgba(255, 255, 255, 0.3)',
        }}
      >
        <h2
          style={{
            fontSize: 50,
            color: 'white',
            margin: 0,
            fontWeight: '500',
          }}
        >
          教练解说
        </h2>
      </div>
      
      {/* 足球场图标装饰 */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          display: 'flex',
          gap: 20,
          opacity: subtitleOpacity,
        }}
      >
        <div style={{ fontSize: 60 }}>⚽</div>
        <div style={{ fontSize: 60, transform: 'rotate(15deg)' }}>🎯</div>
        <div style={{ fontSize: 60, transform: 'rotate(-15deg)' }}>📊</div>
      </div>
    </AbsoluteFill>
  );
};

// ==================== 主体组件（战术演示） ====================

const TacticalAnalysis: React.FC<{
  frame: number;
  fps: number;
}> = ({ frame, fps }) => {
  // 球场入场
  const pitchScale = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });
  
  // 球员数据（4-4-2 阵型进攻演示）- 竖向布局
  const players = [
    // 红队（进攻方，从下往上进攻）
    { id: 1, number: 1, x: 50, y: 90, team: 'red', label: 'GK' },
    { id: 2, number: 2, x: 20, y: 75, team: 'red', label: 'DEF', endX: 15, endY: 65 },
    { id: 3, number: 3, x: 40, y: 75, team: 'red', label: 'DEF' },
    { id: 4, number: 4, x: 60, y: 75, team: 'red', label: 'DEF' },
    { id: 5, number: 5, x: 80, y: 75, team: 'red', label: 'DEF', endX: 85, endY: 65 },
    { id: 6, number: 6, x: 30, y: 55, team: 'red', label: 'MID', endX: 25, endY: 45 },
    { id: 7, number: 7, x: 50, y: 60, team: 'red', label: 'MID' },
    { id: 8, number: 8, x: 70, y: 55, team: 'red', label: 'MID', endX: 75, endY: 45 },
    { id: 9, number: 9, x: 35, y: 40, team: 'red', label: 'FWD', endX: 30, endY: 25 },
    { id: 10, number: 10, x: 65, y: 40, team: 'red', label: 'FWD', endX: 70, endY: 25 },
    // 蓝队（防守方，从上往下防守）
    { id: 11, number: 1, x: 50, y: 10, team: 'blue', label: 'GK' },
    { id: 12, number: 2, x: 20, y: 25, team: 'blue', label: 'DEF' },
    { id: 13, number: 3, x: 40, y: 25, team: 'blue', label: 'DEF' },
    { id: 14, number: 4, x: 60, y: 25, team: 'blue', label: 'DEF' },
    { id: 15, number: 5, x: 80, y: 25, team: 'blue', label: 'DEF' },
  ];
  
  // 传球路线
  const passes = [
    { from: 7, to: 6, color: '#FFD700', delay: 30 },
    { from: 6, to: 9, color: '#00F2FF', delay: 60 },
    { from: 9, to: 10, color: '#FF6B6B', delay: 90 },
  ];

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0a1929 0%, #1a2840 100%)',
      }}
    >
      {/* 战术板标题 */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '10px 40px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          <h2
            style={{
              fontSize: 48,
              color: '#1a472a',
              margin: 0,
              fontWeight: 'bold',
            }}
          >
            4-4-2 进攻战术演示
          </h2>
        </div>
      </div>
      
      {/* 战术板（足球场） */}
      <div
        style={{
          position: 'absolute',
          top: 120,
          left: 40,
          right: 40,
          bottom: 40,
          transform: `scale(${pitchScale})`,
        }}
      >
        {/* 球场背景 */}
        <div
          style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #2d5016 0%, #336619 100%)',
            borderRadius: 8,
            border: '4px solid white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* 球场线条（简化版 - 竖向布局） */}
          <svg
            viewBox="0 0 60 100"
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
            }}
          >
            {/* 中线 */}
            <line x1="0" y1="50" x2="60" y2="50" stroke="white" strokeWidth="0.3" />

            {/* 中圈 */}
            <circle cx="30" cy="50" r="8" fill="none" stroke="white" strokeWidth="0.3" />

            {/* 上方禁区 */}
            <rect x="12" y="0" width="36" height="15" fill="none" stroke="white" strokeWidth="0.3" />

            {/* 下方禁区 */}
            <rect x="12" y="85" width="36" height="15" fill="none" stroke="white" strokeWidth="0.3" />
          </svg>
          
          {/* 传球路线 */}
          {passes.map((pass, idx) => {
            const fromPlayer = players.find(p => p.id === pass.from);
            const toPlayer = players.find(p => p.id === pass.to);
            if (!fromPlayer || !toPlayer) return null;
            
            const passFrame = Math.max(0, frame - pass.delay);
            const progress = interpolate(passFrame, [0, 30], [0, 1], {
              extrapolateRight: 'clamp',
            });
            
            if (progress === 0) return null;
            
            return (
              <svg
                key={idx}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              >
                <defs>
                  <marker
                    id={`arrow-${idx}`}
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <path d="M0,0 L0,6 L9,3 z" fill={pass.color} />
                  </marker>
                </defs>
                <line
                  x1={`${fromPlayer.x}%`}
                  y1={`${fromPlayer.y}%`}
                  x2={`${fromPlayer.x + (toPlayer.x - fromPlayer.x) * progress}%`}
                  y2={`${fromPlayer.y + (toPlayer.y - fromPlayer.y) * progress}%`}
                  stroke={pass.color}
                  strokeWidth="3"
                  strokeDasharray="8,4"
                  markerEnd={`url(#arrow-${idx})`}
                  opacity="0.8"
                />
              </svg>
            );
          })}
          
          {/* 球员 */}
          {players.map((player, idx) => {
            const delay = idx * 2;
            const playerFrame = Math.max(0, frame - delay);
            
            const scale = spring({
              frame: playerFrame,
              fps,
              config: {
                damping: 12,
                stiffness: 100,
              },
            });
            
            // 跑位动画
            let currentX = player.x;
            let currentY = player.y;
            
            if (player.endX && player.endY && frame > 60) {
              const moveProgress = interpolate(frame, [60, 120], [0, 1], {
                extrapolateRight: 'clamp',
              });
              currentX = interpolate(moveProgress, [0, 1], [player.x, player.endX]);
              currentY = interpolate(moveProgress, [0, 1], [player.y, player.endY]);
            }
            
            const isRed = player.team === 'red';
            
            return (
              <div
                key={player.id}
                style={{
                  position: 'absolute',
                  left: `${currentX}%`,
                  top: `${currentY}%`,
                  transform: `translate(-50%, -50%) scale(${scale})`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {/* 球员圆点 */}
                <div
                  style={{
                    width: 76,
                    height: 76,
                    borderRadius: '50%',
                    backgroundColor: isRed ? '#FF4136' : '#0074D9',
                    border: '4px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    fontWeight: 'bold',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                  }}
                >
                  {player.number}
                </div>
                
                {/* 位置标签 */}
                {player.label && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: '4px 12px',
                      backgroundColor: isRed ? 'rgba(255, 65, 54, 0.9)' : 'rgba(0, 116, 217, 0.9)',
                      borderRadius: 6,
                      fontSize: 24,
                      color: 'white',
                      fontWeight: '600',
                    }}
                  >
                    {player.label}
                  </div>
                )}
              </div>
            );
          })}
          
          {/* 战术标注 */}
          {frame > 90 && (
            <div
              style={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                right: 20,
                display: 'flex',
                justifyContent: 'space-around',
                opacity: interpolate(frame, [90, 110], [0, 1], {
                  extrapolateRight: 'clamp',
                }),
              }}
            >
              <div
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'rgba(255, 215, 0, 0.9)',
                  borderRadius: 8,
                  fontSize: 30,
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                }}
              >
                ⚡ 边路插上
              </div>
              <div
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'rgba(0, 242, 255, 0.9)',
                  borderRadius: 8,
                  fontSize: 30,
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                }}
              >
                🎯 斜插跑位
              </div>
              <div
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'rgba(255, 107, 107, 0.9)',
                  borderRadius: 8,
                  fontSize: 30,
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                }}
              >
                📐 三角配合
              </div>
            </div>
          )}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ==================== 结尾组件 ====================

const ClosingScene: React.FC<{
  frame: number;
  fps: number;
}> = ({ frame, fps }) => {
  const scale = spring({
    frame,
    fps,
    config: {
      damping: 12,
      stiffness: 80,
    },
  });
  
  const logoOpacity = interpolate(frame, [30, 60], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1a472a 0%, #2d5a27 50%, #1a472a 100%)',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 总结文字 */}
      <div
        style={{
          padding: '30px 60px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          transform: `scale(${scale})`,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: 88,
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #1a472a 0%, #4CAF50 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0,
            marginBottom: 24,
          }}
        >
          记住这个战术！
        </h1>
        <p
          style={{
            fontSize: 50,
            color: '#666',
            margin: 0,
          }}
        >
          团队配合 &gt; 个人能力
        </p>
      </div>
      
      {/* Logo */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          opacity: logoOpacity,
        }}
      >
        <div
          style={{
            fontSize: 66,
            marginBottom: 12,
          }}
        >
          🦞
        </div>
        <div
          style={{
            fontSize: 50,
            color: 'white',
            fontWeight: 'bold',
            textShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          龙虾教练
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ==================== 主组件 ====================

export const FootballCoachCommentary: React.FC<CoachCommentaryProps> = ({
  audioIntro,
  audioMain,
  audioOutro,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  // 根据配音时长调整时序（30 FPS）
  const introDuration = 141; // 4.7秒
  const mainDuration = 483;  // 16.1秒
  const outroDuration = 183; // 6.1秒
  
  return (
    <AbsoluteFill>
      {/* 开场（0-4.7秒，141帧） */}
      <Sequence from={0} durationInFrames={introDuration}>
        <OpeningScene frame={frame} fps={fps} />
      </Sequence>
      
      {/* 主体（4.7-20.8秒，483帧） */}
      <Sequence from={introDuration} durationInFrames={mainDuration}>
        <TacticalAnalysis frame={Math.max(0, frame - introDuration)} fps={fps} />
      </Sequence>
      
      {/* 结尾（20.8-26.9秒，183帧） */}
      <Sequence from={introDuration + mainDuration} durationInFrames={outroDuration}>
        <ClosingScene frame={Math.max(0, frame - introDuration - mainDuration)} fps={fps} />
      </Sequence>
      
      {/* 音频轨道 */}
      {audioIntro && (
        <Sequence from={0} durationInFrames={introDuration}>
          <Audio src={staticFile(audioIntro)} />
        </Sequence>
      )}
      {audioMain && (
        <Sequence from={introDuration} durationInFrames={mainDuration}>
          <Audio src={staticFile(audioMain)} />
        </Sequence>
      )}
      {audioOutro && (
        <Sequence from={introDuration + mainDuration} durationInFrames={outroDuration}>
          <Audio src={staticFile(audioOutro)} />
        </Sequence>
      )}
    </AbsoluteFill>
  );
};

// ==================== Composition 配置 ====================

export const footballCoachCommentaryConfig = {
  id: 'FootballCoachCommentary',
  component: FootballCoachCommentary,
  durationInFrames: 810, // 27 秒 @ 30 FPS (根据配音时长调整)
  fps: 30,
  width: 1080,
  height: 1920,
};
